import type { CountryCatalog } from "./country-catalogs"
import { supplySources } from "./concepts"
import type { DatasetEntry } from "./datasets"
import { methodologyEntries, type MethodologyEntry, type Signal } from "./methodology"

// Pure helpers that operate on the loader-provided arrays. Consumers used to
// import getCountryCatalog / datasetsForCountry directly from the editorial
// files, which forced those files to be the source-of-truth. Now the source
// of truth is the loader at `_console.docs.tsx`, so the helpers take arrays
// as input.

// Canonical display order for country lists across the docs surface (mega
// menu, coverage index, country switcher): fully-live countries first, then
// "documentation coming soon", then "data coming soon"; alphabetical within
// each group. Use this everywhere countries render as a list so the order is
// identical.
const comingSoonRank = (c: CountryCatalog): number =>
  c.comingSoon === "data" ? 2 : c.comingSoon === "docs" ? 1 : 0

export const sortCountriesForDocs = (
  countries: CountryCatalog[],
): CountryCatalog[] =>
  [...countries].sort(
    (a, b) =>
      comingSoonRank(a) - comingSoonRank(b) || a.name.localeCompare(b.name),
  )

/** Short badge/label for a coming-soon country. */
export const comingSoonLabel = (kind: "docs" | "data"): string =>
  kind === "data" ? "Data coming soon" : "Documentation coming soon"

/** One-line teaser copy for a coming-soon country. */
export const comingSoonBlurb = (kind: "docs" | "data", name: string): string =>
  kind === "data"
    ? `We're building the ${name} datasets now.`
    : `${name} data is available in the platform, and documentation is on the way.`

// Methodology techniques applicable to at least one *live* (non-coming-soon)
// country's signal. Used to hide, from the global Concepts -> Techniques
// surfaces, techniques that only apply to markets that aren't public yet
// (e.g. telco techniques while only the USA / GPS is live). Per-country
// methodology still shows each country its own applicable techniques.
export const liveMethodologyEntries = (
  countries: CountryCatalog[],
): MethodologyEntry[] => {
  const signals = new Set<Signal>()
  for (const c of countries) {
    if (c.comingSoon) continue
    for (const s of signalsForCountry(c.countryCode)) signals.add(s)
  }
  return methodologyEntries.filter((e) =>
    e.appliesTo.some((s) => signals.has(s)),
  )
}

export const getCountryCatalog = (
  countries: CountryCatalog[],
  code: string,
): CountryCatalog | undefined =>
  countries.find((c) => c.code === code.toLowerCase())

export const datasetsForCountry = (
  countries: CountryCatalog[],
  datasets: DatasetEntry[],
  code: string,
): DatasetEntry[] => {
  const cc = getCountryCatalog(countries, code)
  if (!cc) return []
  return datasets.filter((d) => d.country === cc.countryCode)
}

export const getDataset = (
  datasets: DatasetEntry[],
  slug: string,
): DatasetEntry | undefined => datasets.find((d) => d.slug === slug)

// Methodology is surfaced per country, but it is really a property of the
// signal. A country's supply sources carry the signals, so the chain is:
//   country -> supply sources -> signals -> methodology entries.
// A country with no supply recorded yet resolves to no signals, and so to
// no methodology, which is the honest answer rather than a misleading one.

/** Distinct signals serving a country, e.g. ["telco"] for Norway. */
export const signalsForCountry = (countryCode: string): Signal[] => [
  ...new Set(
    supplySources.filter((s) => s.country === countryCode).map((s) => s.signal),
  ),
]

/** Methodology entries that apply to at least one of a country's signals. */
export const methodologyForCountry = (countryCode: string): MethodologyEntry[] => {
  const signals = signalsForCountry(countryCode)
  return methodologyEntries.filter((e) =>
    e.appliesTo.some((s) => signals.includes(s)),
  )
}

/** Look up a methodology entry, scoped to what actually applies in a country. */
export const methodologyEntryForCountry = (
  countryCode: string,
  id: string,
): MethodologyEntry | undefined =>
  methodologyForCountry(countryCode).find((e) => e.id === id)

// Strips a trailing cadence word (Day/Week/Month/Hour/Quarter/Year/Annual,
// adjective or noun form) so cadence can be appended consistently as a
// separate tag — e.g. "Foot Traffic Month" → "Foot Traffic — Monthly".
// Used by the sidebar and by listing rows on the metric-model and
// location-type pages so the same label format is consistent across the
// docs UI.
const CADENCE_NAME_TAIL =
  /\s+(Hourly|Hour|Daily|Day|Weekly|Week|Monthly|Month|Quarterly|Quarter|Yearly|Year|Annual)$/i

export const labelWithCadence = (name: string, cadence: string): string => {
  const stripped = name.replace(CADENCE_NAME_TAIL, "").trim()
  return cadence ? `${stripped} — ${cadence}` : stripped
}

// Strip trailing map-level and cadence segments from an LTM id so all
// cadence × map-level variants share a family id —
//   `places_foot_traffic_day`          → `places_foot_traffic`
//   `trade_areas_day_al2`              → `trade_areas`        (strip _al2, then _day)
//   `trade_areas_month_al4`            → `trade_areas`        (strip _al4, then _month)
//   `cat999_census_visitor_demographics` → unchanged          (no cadence / no _al)
// LTM ids without either tail are returned unchanged so they form a one-
// member family.
const LAYER_LEVEL_TAIL = /_al\d+$/i
const CADENCE_ID_TAIL =
  /_(hourly|hour|daily|day|weekly|week|monthly|month|quarterly|quarter|yearly|year|annual)$/i

export const ltmFamilyId = (ltmId: string): string =>
  ltmId.replace(LAYER_LEVEL_TAIL, "").replace(CADENCE_ID_TAIL, "")

// Canonical "metric concept" name derived from an LTM display name. Used to
// collapse LTMs that share a concept but live under different LTM ids
// (e.g. `places_foot_traffic_month` and `cbg_foot_traffic_month` both have
// displayName "Foot Traffic Month" → canonical "Foot Traffic"). Also folds
// in map-level qualifiers and "by X" suffixes, so:
//   "Foot Traffic Day"                → "Foot Traffic"
//   "Foot Traffic Annual (Multi-Source Test)" → "Foot Traffic"
//   "Trade Areas by CBG"              → "Trade Areas"
//   "Trade Areas Day (AL2)"           → "Trade Areas"
//   "Demographics"                    → "Demographics"
//
// Returns the trimmed display name unchanged when no qualifier matches.
const PAREN_TAIL = /\s*\([^)]+\)\s*$/
const BY_TAIL = /\s+by\s+.+$/i
// Some catalogs bake the location type into the display name (Nordic
// "Area Foot Traffic Day" vs USA "Foot Traffic Month"). The concept pages
// are already scoped per location type, so a leading type word is
// redundant and would split one concept into two ("Area Foot Traffic" vs
// "Foot Traffic" pages with disjoint datasets). Strip it for grouping:
//   "Area Foot Traffic Day"          → "Foot Traffic"
//   "Area Roamers Foot Traffic Month" → "Roamers Foot Traffic"
const TYPE_NAME_HEAD = /^(places?|areas?|ways?)\s+/i

export const metricCanonicalName = (displayName: string): string => {
  let s = displayName
  // Apply paren-stripping repeatedly so "X (a) (b)" → "X".
  while (PAREN_TAIL.test(s)) s = s.replace(PAREN_TAIL, "").trim()
  s = s.replace(BY_TAIL, "").trim()
  s = s.replace(CADENCE_NAME_TAIL, "").trim()
  s = s.replace(TYPE_NAME_HEAD, "").trim()
  return s
}

// URL-safe slug for a canonical metric concept (no location type). Kept as
// a building block — callers that want a clickable overview URL should use
// `canonicalMetricSlugForType` so the slug is scoped to a single location
// type and the overview reads as "Foot Traffic on Place" / "Foot Traffic on
// Area" rather than mixing them on one page.
//   "Foot Traffic"    → "foot-traffic"
//   "Trade Areas"     → "trade-areas"
//   "Visit Length"    → "visit-length"
export const canonicalMetricSlug = (displayName: string): string =>
  metricCanonicalName(displayName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

// Per-location-type slug, e.g. `foot-traffic-place`, `trade-areas-area`.
// Each location-type variant of a metric concept gets its own overview page
// so the listing isn't mixed across types.
export const canonicalMetricSlugForType = (
  displayName: string,
  locationType: string,
): string => `${canonicalMetricSlug(displayName)}-${locationType}`

// Parse a per-location-type slug back into (baseSlug, locationType). Returns
// null when the id doesn't end with one of the canonical location-type
// suffixes — useful in the route loader for distinguishing canonical-overview
// URLs from concrete LTM ids and legacy concept slugs.
const TYPE_SLUG_SUFFIX = /-(place|area|way)$/
export const parseCanonicalMetricSlug = (
  id: string,
): { baseSlug: string; locationType: "place" | "area" | "way" } | null => {
  const m = TYPE_SLUG_SUFFIX.exec(id)
  if (!m) return null
  return {
    baseSlug: id.slice(0, -m[0].length),
    locationType: m[1] as "place" | "area" | "way",
  }
}

// Editorial cross-links between metric concepts, matched on the canonical
// concept name. These are the "you probably also want" pairings that come up
// in customer conversations — most often the trade-area question behind any
// audience metric: "where do these visitors actually come from?".
//
// Matching is by regex against the canonical name rather than by id, so a
// pairing survives the naming differences between country catalogs
// ("Trade Areas", "Trade Areas by CBG", "Trade Area Month" all match).
const RELATED_CONCEPT_RULES: { when: RegExp; suggest: RegExp[] }[] = [
  { when: /visitor\s+profile/i, suggest: [/trade\s+area/i] },
  { when: /demographic/i, suggest: [/trade\s+area/i] },
  {
    when: /trade\s+area/i,
    suggest: [/visitor\s+profile/i, /demographic/i, /visitor\s+journey/i],
  },
  { when: /visitor\s+journey/i, suggest: [/trade\s+area/i] },
]

/**
 * Concepts to cross-link from a metric concept page. Only concepts that
 * actually have datasets in the same location type are returned, so we never
 * link a reader to an empty page.
 *
 * `authoredBody` is the metric's authored description. Anything it already
 * points the reader at is dropped: several metrics carry a hand-written
 * "Related metrics" section, and repeating one of its links as an automatic
 * suggestion just says the same thing twice.
 */
export const relatedMetricConcepts = (
  datasets: DatasetEntry[],
  conceptName: string,
  locationType: string,
  authoredBody = "",
): { name: string; to: string }[] => {
  const rule = RELATED_CONCEPT_RULES.find((r) => r.when.test(conceptName))
  if (!rule) return []

  // Collapse this location type's datasets into concepts, the same way the
  // location-type and topbar surfaces do.
  const membersByConcept = new Map<string, { ltm: string }[]>()
  for (const d of datasets) {
    if (d.locationType !== locationType) continue
    const concept = metricCanonicalName(d.name)
    if (concept.toLowerCase() === conceptName.toLowerCase()) continue
    const members = membersByConcept.get(concept)
    if (members) {
      if (!members.some((m) => m.ltm === d.ltm)) members.push({ ltm: d.ltm })
    } else {
      membersByConcept.set(concept, [{ ltm: d.ltm }])
    }
  }

  // Singular, lowercase form — so "Trade Area" and "Trade Areas" (which arise
  // from differently-named listings of the same concept) compare equal, both
  // against each other and against the authored text.
  const key = (s: string) => s.toLowerCase().trim().replace(/s\b/g, "")
  const authored = key(authoredBody)

  const candidates = [...membersByConcept.entries()]
    .filter(([concept]) => rule.suggest.some((p) => p.test(concept)))
    .filter(([concept]) => !authored.includes(key(concept)))
    .map(([concept, members]) => ({
      name: concept,
      // Single-variant concept → link straight to that LTM page; multi-variant
      // → the typed canonical overview.
      to:
        members.length === 1
          ? `/docs/concepts/metrics/${members[0].ltm}`
          : `/docs/concepts/metrics/${canonicalMetricSlugForType(concept, locationType)}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

  // Two spellings of one concept ("Trade Area" / "Trade Areas") can survive as
  // separate entries; keep one per concept, and one per destination.
  const seen = new Set<string>()
  return candidates.filter((c) => {
    const k = `${key(c.name)}|${c.to}`
    if (seen.has(key(c.name)) || seen.has(c.to)) return false
    seen.add(key(c.name))
    seen.add(c.to)
    return !!k
  })
}

// Auto-generated short description for a country catalog. Used by the docs
// topbar mega menu and the coverage index card so the meta line has the same
// structure across countries. The editorial `cc.description` blob is reserved
// for the country detail page itself.
//
// Output shape: `{N} datasets across {types} · {cadence summary} · {supply}`,
// with sections omitted when they have nothing to say.
export const buildCountrySummary = (
  countries: CountryCatalog[],
  datasets: DatasetEntry[],
  code: string,
): string => {
  const cc = getCountryCatalog(countries, code)
  if (!cc) return ""
  const ds = datasetsForCountry(countries, datasets, code)
  if (ds.length === 0) return "Coming soon"

  const parts: string[] = []
  parts.push(`${ds.length} dataset${ds.length === 1 ? "" : "s"} across ${formatLocationTypes(ds)}`)

  const cadence = formatCadenceRange(ds)
  if (cadence) parts.push(cadence)

  const supply = supplySources.find((s) => s.country === cc.countryCode)
  if (supply) parts.push(supply.shortName)

  return parts.join(" · ")
}

const LOCATION_TYPE_ORDER: DatasetEntry["locationType"][] = ["place", "area", "way"]

const formatLocationTypes = (ds: DatasetEntry[]): string => {
  const present = new Set(ds.map((d) => d.locationType))
  const ordered = LOCATION_TYPE_ORDER.filter((t) => present.has(t))
  return ordered.length > 0 ? ordered.join(", ") : "—"
}

const CADENCE_ORDER: DatasetEntry["cadence"][] = [
  "Hourly",
  "Daily",
  "Weekly",
  "Monthly",
  "Quarterly",
  "Yearly",
]

const formatCadenceRange = (ds: DatasetEntry[]): string => {
  const present = new Set(ds.map((d) => d.cadence))
  const ordered = CADENCE_ORDER.filter((c) => present.has(c))
  if (ordered.length === 0) return ""
  if (ordered.length === 1) return `${ordered[0].toLowerCase()} cadence`
  const first = ordered[0].toLowerCase()
  const last = ordered[ordered.length - 1].toLowerCase()
  return `${first}–${last} cadences`
}
